import { useState } from 'react';
import { Save, RotateCcw, Shield, Zap, Target } from 'lucide-react';

export default function Settings() {
  const [minHopCount, setMinHopCount] = useState(3);
  const [maxHopCount, setMaxHopCount] = useState(20);
  const [minTransactionThreshold, setMinTransactionThreshold] = useState(5);
  const [autoBlacklistThreshold, setAutoBlacklistThreshold] = useState(80);
  const [enableWrappedTokenDetection, setEnableWrappedTokenDetection] = useState(true);
  const [enableCircularFlowDetection, setEnableCircularFlowDetection] = useState(true);

  const presets = [
    { name: 'Strict', minHop: 2, maxHop: 25, minTx: 3, threshold: 70 },
    { name: 'Balanced', minHop: 3, maxHop: 20, minTx: 5, threshold: 80 },
    { name: 'Relaxed', minHop: 5, maxHop: 15, minTx: 10, threshold: 90 },
  ];

  const applyPreset = (preset: typeof presets[0]) => {
    setMinHopCount(preset.minHop);
    setMaxHopCount(preset.maxHop);
    setMinTransactionThreshold(preset.minTx);
    setAutoBlacklistThreshold(preset.threshold);
  };

  return (
    <div className="max-w-5xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
          Detection Settings
        </h1>
        <p className="text-gray-400 text-lg">
          Configure abuse detection thresholds and sensitivity
        </p>
      </div>

      {/* Presets */}
      <div className="mb-6 p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
        <h2 className="text-xl font-bold text-white mb-4">Quick Presets</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {presets.map((preset) => (
            <button
              key={preset.name}
              onClick={() => applyPreset(preset)}
              className="p-4 rounded-lg bg-gray-700/30 hover:bg-gray-700/50 border border-gray-600 hover:border-blue-500 transition-all text-left group"
            >
              <div className="flex items-center gap-2 mb-2">
                <Shield size={20} className="text-blue-400" />
                <h3 className="font-semibold text-white group-hover:text-blue-400 transition-colors">
                  {preset.name}
                </h3>
              </div>
              <p className="text-sm text-gray-400">
                {preset.name === 'Strict' && 'Catches more, may have false positives'}
                {preset.name === 'Balanced' && 'Recommended for most use cases'}
                {preset.name === 'Relaxed' && 'Fewer false positives, may miss some'}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Detection Parameters */}
      <div className="mb-6 p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
        <h2 className="text-xl font-bold text-white mb-6">Detection Parameters</h2>
        
        <div className="space-y-6">
          {/* Min Hop Count */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-300">
                Minimum Hop Count
              </label>
              <span className="text-lg font-bold text-blue-400">{minHopCount}</span>
            </div>
            <input
              type="range"
              min="1"
              max="10"
              value={minHopCount}
              onChange={(e) => setMinHopCount(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <p className="mt-1 text-xs text-gray-400">
              Minimum transaction hops to flag as suspicious
            </p>
          </div>

          {/* Max Hop Count */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-300">
                Maximum Hop Count
              </label>
              <span className="text-lg font-bold text-blue-400">{maxHopCount}</span>
            </div>
            <input
              type="range"
              min="5"
              max="30"
              value={maxHopCount}
              onChange={(e) => setMaxHopCount(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <p className="mt-1 text-xs text-gray-400">
              Maximum hops to analyze in fund flow
            </p>
          </div>

          {/* Min Transaction Threshold */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-300">
                Minimum Transaction Threshold
              </label>
              <span className="text-lg font-bold text-blue-400">{minTransactionThreshold}</span>
            </div>
            <input
              type="range"
              min="1"
              max="20"
              value={minTransactionThreshold}
              onChange={(e) => setMinTransactionThreshold(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <p className="mt-1 text-xs text-gray-400">
              Minimum transactions required for pattern detection
            </p>
          </div>

          {/* Auto-Blacklist Threshold */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-300">
                Auto-Blacklist Threshold
              </label>
              <span className="text-lg font-bold text-red-400">{autoBlacklistThreshold}</span>
            </div>
            <input
              type="range"
              min="50"
              max="100"
              value={autoBlacklistThreshold}
              onChange={(e) => setAutoBlacklistThreshold(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-500"
            />
            <p className="mt-1 text-xs text-gray-400">
              Risk score threshold for automatic blacklisting
            </p>
          </div>
        </div>
      </div>

      {/* Feature Toggles */}
      <div className="mb-6 p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
        <h2 className="text-xl font-bold text-white mb-6">Detection Features</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-700/30">
            <div className="flex items-center gap-3">
              <Zap size={20} className="text-yellow-400" />
              <div>
                <div className="font-medium text-white">Wrapped Token Detection</div>
                <div className="text-sm text-gray-400">Detect abuse using sVERSE transfers</div>
              </div>
            </div>
            <button
              onClick={() => setEnableWrappedTokenDetection(!enableWrappedTokenDetection)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                enableWrappedTokenDetection ? 'bg-blue-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  enableWrappedTokenDetection ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-700/30">
            <div className="flex items-center gap-3">
              <Target size={20} className="text-green-400" />
              <div>
                <div className="font-medium text-white">Circular Flow Detection</div>
                <div className="text-sm text-gray-400">Identify circular transaction patterns</div>
              </div>
            </div>
            <button
              onClick={() => setEnableCircularFlowDetection(!enableCircularFlowDetection)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                enableCircularFlowDetection ? 'bg-blue-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  enableCircularFlowDetection ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <button
          onClick={() => alert('Settings saved!')}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <Save size={20} />
          Save Configuration
        </button>
        <button
          onClick={() => {
            setMinHopCount(3);
            setMaxHopCount(20);
            setMinTransactionThreshold(5);
            setAutoBlacklistThreshold(80);
          }}
          className="px-6 py-4 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200 flex items-center gap-2"
        >
          <RotateCcw size={20} />
          Reset to Defaults
        </button>
      </div>

      {/* Info */}
      <div className="mt-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
        <p className="text-sm text-gray-300">
          <strong className="text-blue-400">Tip:</strong> Start with the "Balanced" preset and adjust based on your results. Lower thresholds = more sensitive detection.
        </p>
      </div>
    </div>
  );
}
